/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITextField, ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useLocalization } from 'src/core/hooks/useLocalization';

export type TagInputFieldProps = {
    /**
     * An array of characters that if pressed, will
     * be considered as a submission to the value.
     * Examples include 'Enter', 'comma', etc.
     */
    submitChars: string[];

    /**
     * Callback for when the field is empty and a
     * backspace key was pressed.
     */
    onEmptyFieldBackspace: () => void;

    /**
     * Callback for when the field is submitted using
     * one of the characters defined in the submit
     * chars parameter.
     */
    onSubmit: (value: string[]) => void;

    /**
     * Whether the user can add values.
     */
    isAddingValuesDisabled?: boolean;

    /**
     * Whether the value is required or not.
     */
    isRequired?: boolean;

    /**
     * weather autofocus enabled or not
     */
    autoFocus?: boolean;
};

const messages = defineMessages({
    fieldPlaceholder: {
        id: 'core.tagInputField.fieldPlaceholder',
        defaultMessage: 'Type in value and press enter...'
    },
    fieldAriaLabel: {
        id: 'core.tagInputField.fieldAriaLabel',
        defaultMessage: 'Type in value and press enter'
    }
});

const getClassName = classNamesFunction<ITextFieldStyleProps, ITextFieldStyles>();

const fabricFieldClassName = getClassName({
    field: {
        fontSize: '12px',
        width: '200px',
        selectors: { '::placeholder': { fontSize: '12px' } }
    },
    fieldGroup: {
        backgroundColor: 'transparent',
        height: '26px'
    }
});

const disabledFabricClassName = getClassName({
    field: {
        fontSize: '12px',
        width: '200px',
        selectors: { '::placeholder': { fontSize: '12px' } },
        backgroundColor: 'transparent'
    },
    fieldGroup: {
        backgroundColor: 'transparent',
        height: '26px'
    }
});

/**
 * Represents an input field suitable for use with tags
 * inputting and management.
 *
 * @param props The input for the text field.
 */
export const TagInputField = React.forwardRef(
    (props: TagInputFieldProps, phraseListValuesContainerRef: React.MutableRefObject<ITextField>) => {
        const {
            submitChars = ['Enter', ','],
            onEmptyFieldBackspace,
            onSubmit,
            isAddingValuesDisabled,
            isRequired = false,
            autoFocus = true
        } = props;
        const [fieldValue, setFieldValue] = React.useState<string>('');
        const { localize } = useLocalization();

        /**
         * Reacts to the key down event based on the following rules:
         *
         * - If one of the submission keys was pressed, the value
         *   is submitted.
         * - If Escape was pressed, the value of the field is cleared.
         * - If Backspace was pressed and the field is empty, the
         *   empty clear callback is fired.
         *
         * Using key down event here to fire the callback after the setFieldValue fires
         * and prevent the conflicts.
         *
         * @param event The event that caused the callback to fire.
         */
        const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
            if (submitChars.find(c => c === event.key)) {
                const inputVal = fieldValue.trim();
                if (!inputVal) {
                    setFieldValue('');

                    return;
                }
                setFieldValue('');
                onSubmit([inputVal]);
            } else if (event.key === 'Escape' && fieldValue.length) {
                setFieldValue('');
            } else if (event.key === 'Backspace' && fieldValue.length === 0) {
                onEmptyFieldBackspace();
            }
        };

        /**
         * Reacts to losing focus from the text field based on the following rules:
         *
         * If focus is lost while there is new text written, it is submitted as a new entry
         * If foucs is lost either while the field is empty or duplicate text, it is cleared
         */

        const onBlur = (): void => {
            const inputVal = fieldValue.trim();
            if (!inputVal) {
                setFieldValue('');

                return;
            }
            setFieldValue('');
            onSubmit([inputVal]);
        };

        /**
         * Checks if the pasted data is of text type, and if so, splits
         * on the commas.
         *
         * @param event The paste event that was fired by the field.
         */
        const onPaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
            setFieldValue('');
            event.preventDefault();
            const pasteData = event.clipboardData;

            if (pasteData.types.indexOf('text/plain') === -1) {
                return;
            }

            const text = pasteData.getData('text/plain');
            const items = text
                .split(',')
                .map(value => value.trim())
                .filter(value => value.length);

            onSubmit(items);
        };

        return (
            <LuisTextField
                data-automation-id="typeValueTextarea"
                borderless
                autoFocus={autoFocus}
                onPaste={onPaste}
                value={fieldValue}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                styles={isAddingValuesDisabled ? disabledFabricClassName : fabricFieldClassName}
                ref={phraseListValuesContainerRef}
                ariaLabel={localize(messages.fieldAriaLabel)}
                placeholder={!isAddingValuesDisabled ? localize(messages.fieldPlaceholder) : ''}
                onChange={(_, value) => (submitChars.find(c => c === value) ? null : setFieldValue(value))}
                disabled={isAddingValuesDisabled}
                required={isRequired}
            />
        );
    }
);
